import { ImageIcon } from 'lucide-react'
import {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
  type SyntheticEvent,
} from 'react'
import { Design_ } from 'src/api/db/apiDesigns'

import { getDimensions } from '../../utils/helpers'
import { Image } from '../ui'
import { cn } from '../ui/utils'
import { getFilterStyle } from './utils'
import { useLocalization } from '../../hooks/useLocalization'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import { Badge } from '../ui/badge'
import { Row } from '../Semantic/all'
import { Lens } from '../lens'

type DisplayLayout = {
  width: number
  offsetX: number
  offsetY: number
}

function computeObjectContainLayout(
  containerWidth: number,
  containerHeight: number,
  naturalWidth: number,
  naturalHeight: number
): DisplayLayout | null {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    naturalWidth <= 0 ||
    naturalHeight <= 0
  ) {
    return null
  }

  const imageAspect = naturalWidth / naturalHeight
  const containerAspect = containerWidth / containerHeight

  if (imageAspect > containerAspect) {
    const displayWidth = containerWidth
    const displayHeight = containerWidth / imageAspect
    return {
      width: displayWidth,
      offsetX: 0,
      offsetY: (containerHeight - displayHeight) / 2,
    }
  }

  const displayWidth = containerHeight * imageAspect
  return {
    width: displayWidth,
    offsetX: (containerWidth - displayWidth) / 2,
    offsetY: 0,
  }
}

export const DesignImage = ({
  logo_url,
  image_url,
  design,
  imageClassName,
  containerClassName,
  onClick,
  style,
  onImageLoad,
  onImageError,
  transitionDuration = 0,
  klayder_recommends,
  withLens = false,
}: {
  logo_url: string | null
  image_url: string
  design: Omit<Design_, 'design_url' | 'image_url'>
  imageClassName?: CSSProperties | string
  containerClassName?: CSSProperties | string
  onClick?: () => void
  style?: CSSProperties
  onImageLoad?: () => void
  onImageError?: () => void
  transitionDuration?: number // Duration in seconds to wait before measuring
  klayder_recommends?: boolean
  withLens?: boolean
}) => {
  const { t } = useLocalization()
  const [imageError, setImageError] = useState(false)
  const [displayLayout, setDisplayLayout] = useState<DisplayLayout>({
    width: 0,
    offsetX: 0,
    offsetY: 0,
  })
  const cardRef = useRef<HTMLDivElement>(null)
  const naturalSizeRef = useRef({ width: 0, height: 0 })
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const design_: Partial<Design_> = {
    brightness: design?.brightness ?? 100,
    colorFilter: design?.colorFilter ?? null,
    comment: design?.comment ?? null,
    created_at: design?.created_at ?? new Date().toISOString(),
    hue: design?.hue ?? 0,
    id: design?.id ?? 0,
    opacity: design?.opacity ?? 1,
    order: design?.order ?? 0,
    quote_item_id: design?.quote_item_id ?? 0,
    rotation_x: design?.rotation_x ?? 0,
    rotation_y: design?.rotation_y ?? 0,
    rotation_z: design?.rotation_z ?? 0,
    saturation: design?.saturation ?? 100,
    warp_transform: design?.warp_transform ?? null,
    w: design?.w ?? 0,
    h: design?.h ?? 0,
    x: design?.x ?? 0,
    y: design?.y ?? 0,
  }

  const updateLayout = useCallback(() => {
    const element = cardRef.current
    if (!element) return

    const { width: naturalWidth, height: naturalHeight } = naturalSizeRef.current

    if (naturalWidth > 0 && naturalHeight > 0) {
      const layout = computeObjectContainLayout(
        element.clientWidth,
        element.clientHeight,
        naturalWidth,
        naturalHeight
      )
      if (layout) {
        setDisplayLayout(layout)
        return
      }
    }

    const width = element.getBoundingClientRect().width
    if (width > 0) {
      setDisplayLayout({ width, offsetX: 0, offsetY: 0 })
    }
  }, [])

  useEffect(() => {
    naturalSizeRef.current = { width: 0, height: 0 }
    setDisplayLayout({ width: 0, offsetX: 0, offsetY: 0 })
  }, [image_url])

  // Measure layout when the container resizes
  useEffect(() => {
    const element = cardRef.current
    if (!element) return

    const measureAfterTransition = () => {
      requestAnimationFrame(() => {
        updateLayout()
        setTimeout(updateLayout, 200)
      })
    }

    let timeoutId: number | null = null
    if (transitionDuration > 0) {
      timeoutId = window.setTimeout(
        measureAfterTransition,
        transitionDuration * 1000
      )
    } else {
      measureAfterTransition()
    }

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(() => updateLayout())
      resizeObserverRef.current.observe(element)
    } else {
      window.addEventListener('resize', updateLayout)
    }

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      } else {
        window.removeEventListener('resize', updateLayout)
      }
    }
  }, [transitionDuration, updateLayout])

  const printArea = getDimensions({
    x: design_.x,
    y: design_.y,
    w: design_.w,
    h: design_.h,
    newWidth: displayLayout.width,
  })

  const logoLeft = printArea.x + displayLayout.offsetX
  const logoTop = printArea.y + displayLayout.offsetY

  const LogoOverlay = () =>
    logo_url && (
      <Image
        src={logo_url}
        alt="Design"
        className={cn('absolute')}
        style={{
          objectFit: 'contain',
          width: `${printArea.width}px`,
          height: `${printArea.height}px`,
          left: `${logoLeft}px`,
          top: `${logoTop}px`,
          transform: design_.warp_transform
            ? design_.warp_transform
            : `rotateX(${design_.rotation_x || 0}deg) rotateY(${
                design_.rotation_y || 0
              }deg) rotateZ(${design_.rotation_z || 0}deg)`,
          opacity: design_.opacity || 1,
          filter: getFilterStyle(design_),
        }}
      />
    )

  const handleProductImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    naturalSizeRef.current = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    }

    const remeasure = () => updateLayout()

    if (transitionDuration > 0) {
      setTimeout(remeasure, transitionDuration * 1000)
    } else {
      requestAnimationFrame(remeasure)
    }

    onImageLoad?.()
  }

  const productImageBlock = (
    <>
      <Image
        src={image_url || ''}
        alt="Product"
        className={cn(
          `w-full h-full object-contain rounded-lg`,
          imageClassName
        )}
        onError={() => {
          setImageError(true)
          onImageError?.()
          onImageLoad?.() // Call even on error so UI doesn't get stuck
        }}
        onLoad={handleProductImageLoad}
      />
      <LogoOverlay />
    </>
  )

  return (
    <>
      <div
        ref={cardRef}
        className={cn(
          'relative overflow-hidden rounded-md',
          containerClassName,
          onClick && 'cursor-pointer hover:opacity-70'
        )}
        style={style}
        onClick={onClick}
      >
        {imageError || !image_url ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
            <div className="flex flex-col items-center text-gray-400">
              <ImageIcon className="w-12 h-12 mb-2" />
              <span className="text-sm">No image available</span>
            </div>
          </div>
        ) : withLens ? (
          <Lens zoomFactor={2.5} lensSize={260}>
            {productImageBlock}
          </Lens>
        ) : (
          productImageBlock
        )}

        <Row className="absolute top-3 left-3 gap-2 z-30">
          {klayder_recommends && (
            <Badge className="px-2 py-1.5 font-normal bg-white/80 rounded-full gap-1">
              <Image
                src={IMAGE_URLS.logos.klayderSymbol}
                alt="Klayder Symbol"
                className="w-4 h-4 invert"
              />

              <p className="text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-transparent bg-clip-text text-normal">
                {t('Klayder recommends', 'Klayder rekommenderar')}
              </p>
            </Badge>
          )}
        </Row>
      </div>
    </>
  )
}
